import React, { useState, useEffect } from 'react';
import { ClientLayout } from '../../../components/layout/ClientLayout';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { supabase } from '../../../lib/supabase';
import { CreditCard, Search, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from '../../../lib/toast';
import Papa from 'papaparse';
import { Link } from 'react-router-dom';

interface Transaction {
  id: string;
  amount: number;
  created_at: string;
  status: string;
  payment_method: string;
  phone_number: string;
  votes_count: number;
  contestant: {
    name: string;
    unique_code: string;
    contest: {
      id: string;
      name: string;
      slug: string;
    };
  };
}

export const TransactionList = () => {
  const { user } = useCheckPermissions(['view_transactions']);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [contestFilter, setContestFilter] = useState('all');
  const itemsPerPage = 20;

  const paymentMethodOptions = [
    { value: 'all', label: 'Toutes les méthodes' },
    { value: 'mobile_money', label: 'Mobile Money' },
    { value: 'card', label: 'Paypal / CB' }
  ];

  const dateOptions = [
    { value: 'all', label: 'Toutes les dates' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'week', label: '7 derniers jours' },
    { value: 'month', label: '30 derniers jours' },
    { value: 'year', label: '12 derniers mois' }
  ];

  const [contestOptions, setContestOptions] = useState([
    { value: 'all', label: 'Tous les concours' }
  ]);

  // Charger la liste des concours
  useEffect(() => {
    const fetchContests = async () => {
      if (!user) return;

      try {
        const { data: contests, error } = await supabase
          .from('contests')
          .select('id, name, slug')
          .in('id', user.permissions.contests)
          .order('name');

        if (error) throw error;

        setContestOptions([
          { value: 'all', label: 'Tous les concours' },
          ...contests.map((contest) => ({
            value: contest.id,
            label: contest.name
          }))
        ]);
      } catch (error) {
        console.error('Erreur lors du chargement des concours:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger la liste des concours",
          variant: "destructive"
        });
      }
    };

    fetchContests();
  }, [user]);

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace('XAF', 'FCFA');
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'mobile_money':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'paypal':
      case 'card':
        return 'text-blue-500 bg-blue-500/10';
      default:
        return 'text-gray-500 bg-gray-500/10';
    }
  };

  const formatPaymentMethod = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'mobile_money':
        return 'Mobile Money';
      case 'paypal':
      case 'card':
        return 'Paypal / CB';
      default:
        return method || 'Inconnu';
    }
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: fr });
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        
        // Récupérer les transactions pour les concours autorisés
        let query = supabase
          .from('transactions')
          .select(`
            id,
            amount,
            created_at,
            status,
            payment_method,
            phone_number,
            votes_count,
            contestants!inner (
              id,
              name,
              unique_code,
              contest_id,
              contests!inner (
                id,
                name,
                slug
              )
            )
          `)
          .in('contestants.contests.id', user.permissions.contests)
          .eq('status', 'completed')
          .order('created_at', { ascending: false });

        // Ajouter les filtres conditionnellement
        if (paymentMethodFilter !== 'all') {
          query = query.eq('payment_method', paymentMethodFilter);
        }

        if (contestFilter !== 'all') {
          query = query.eq('contestants.contests.id', contestFilter);
        }

        // Exécuter la requête
        const { data, error } = await query;

        if (error) throw error;

        // Filtrer les transactions selon la date
        let filteredData = data || [];
        if (dateFilter !== 'all') {
          const now = new Date();
          const filterDate = new Date();
          
          switch (dateFilter) {
            case 'today':
              filterDate.setHours(0, 0, 0, 0);
              break;
            case 'week':
              filterDate.setDate(now.getDate() - 7);
              break;
            case 'month':
              filterDate.setDate(now.getDate() - 30);
              break;
            case 'year':
              filterDate.setFullYear(now.getFullYear() - 1);
              break;
          }
          
          filteredData = filteredData.filter(tx => 
            new Date(tx.created_at) >= filterDate
          );
        }

        // Transformer les transactions pour l'affichage
        const processedTransactions = filteredData.map(tx => ({
          id: tx.id,
          amount: Number(tx.amount),
          created_at: tx.created_at,
          status: tx.status,
          payment_method: tx.payment_method,
          phone_number: tx.phone_number,
          votes_count: tx.votes_count,
          contestant: {
            name: tx.contestants?.name,
            unique_code: tx.contestants?.unique_code,
            contest: {
              name: tx.contestants?.contests?.name,
              slug: tx.contestants?.contests?.slug
            }
          }
        }));

        setTransactions(processedTransactions);
        setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
      } catch (error) {
        console.error('Erreur lors du chargement des transactions:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les transactions',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [user, paymentMethodFilter, contestFilter, dateFilter]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Réinitialiser à la première page lors d'une nouvelle recherche
  };

  const filteredTransactions = transactions.filter(tx => {
    if (!searchTerm) return true;
    
    const searchValue = searchTerm.toLowerCase();
    return (
      tx.id.toLowerCase().includes(searchValue) ||
      tx.contestant?.name?.toLowerCase().includes(searchValue) ||
      tx.contestant?.contest?.name?.toLowerCase().includes(searchValue) ||
      tx.phone_number?.toLowerCase().includes(searchValue) ||
      tx.payment_method?.toLowerCase().includes(searchValue) ||
      formatPrice(tx.amount).toLowerCase().includes(searchValue) ||
      formatDate(tx.created_at).toLowerCase().includes(searchValue)
    );
  });

  // Pagination côté client
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleExport = async () => {
    try {
      // Récupérer toutes les transactions sans pagination
      let query = supabase
        .from('transactions')
        .select(`
          id,
          amount,
          created_at,
          status,
          payment_method,
          phone_number,
          votes_count,
          contestants!inner (
            name,
            unique_code,
            contest_id,
            contests!inner (
              name,
              slug
            )
          )
        `)
        .in('contestants.contests.id', user.permissions.contests)
        .eq('status', 'completed')
        .order('created_at', { ascending: false });

      // Ajouter les filtres conditionnellement
      if (paymentMethodFilter !== 'all') {
        query = query.eq('payment_method', paymentMethodFilter);
      }

      if (contestFilter !== 'all') {
        query = query.eq('contestants.contests.id', contestFilter);
      }

      // Exécuter la requête
      const { data, error } = await query;

      if (error) throw error;

      if (!data || data.length === 0) {
        toast({
          title: 'Aucune donnée',
          description: 'Aucune transaction à exporter.',
          variant: 'destructive'
        });
        return;
      }

      // Filtrer par date si nécessaire
      let exportData = data;
      if (dateFilter !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        
        switch (dateFilter) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            filterDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            filterDate.setDate(now.getDate() - 30);
            break;
          case 'year':
            filterDate.setFullYear(now.getFullYear() - 1);
            break;
        }
        
        exportData = exportData.filter(tx => 
          new Date(tx.created_at) >= filterDate
        );
      }

      // Formater les données pour le CSV
      const csvData = exportData.map(tx => ({
        'Date': formatDate(tx.created_at),
        'Concours': tx.contestants?.contests?.name || '',
        'Candidat': tx.contestants?.name || '',
        'Votes': tx.votes_count || 0,
        'Montant': formatPrice(tx.amount),
        'Méthode': formatPaymentMethod(tx.payment_method || ''),
        'N° Votant': tx.phone_number || '',
      }));

      // Convertir en CSV avec Papa Parse
      const csv = Papa.unparse(csvData, {
        delimiter: ',',
        header: true,
        quotes: true
      });

      // Créer et télécharger le fichier
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = format(new Date(), "yyyy-MM-dd_HH-mm", { locale: fr });
      
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Succès',
        description: 'Les transactions ont été exportées avec succès.',
        variant: 'default'
      });
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'exporter les transactions',
        variant: 'destructive'
      });
    }
  };

  return (
    <ClientLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 pt-20">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-white">Transactions</h1>
            <p className="mt-2 text-sm text-gray-400">
              Liste de toutes les transactions pour vos concours
            </p>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <Button
              variant="outline"
              className="w-full flex items-center justify-center sm:w-auto"
              onClick={handleExport}
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </Button>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
            <Input
              type="text"
              placeholder="Rechercher par nom de candidat, concours ou numéro..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-yellow-400/50 focus:ring-yellow-400/30"
            />
          </div>
          
          <div className="flex gap-4">
            <Select
              value={paymentMethodFilter}
              onValueChange={setPaymentMethodFilter}
              options={paymentMethodOptions}
              placeholder="Méthode de paiement"
              className="w-48"
            />
            <Select
              value={contestFilter}
              onValueChange={setContestFilter}
              options={contestOptions}
              placeholder="Concours"
              className="w-64"
            />
            <Select
              value={dateFilter}
              onValueChange={setDateFilter}
              options={dateOptions}
              placeholder="Date"
              className="w-48"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="mt-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-yellow-400" />
          </div>
        ) : (
          <>
            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead>
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-white sm:pl-0">
                          Candidat
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-white">
                          Concours
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-white">
                          Montant
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-white">
                          Votes
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-white">
                          Méthode
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-white">
                          N° Votant
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-white">
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {isLoading ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-4 text-center text-gray-400">
                            Chargement...
                          </td>
                        </tr>
                      ) : paginatedTransactions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-4 text-center text-gray-400">
                            Aucune transaction trouvée
                          </td>
                        </tr>
                      ) : (
                        paginatedTransactions.map((transaction) => (
                          <tr key={transaction.id} className="group hover:bg-white/5 transition-colors duration-150">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 group-hover:text-white transition-colors">
                              <Link 
                                to={`/${transaction.contestant.unique_code}`} 
                                className="text-white hover:text-yellow-400 transition-colors"
                              >
                                {transaction.contestant.name}
                              </Link>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 group-hover:text-white transition-colors">
                              <Link 
                                to={`/c/${transaction.contestant.contest.slug}`} 
                                className="font-medium text-white hover:text-yellow-400 transition-colors"
                              >
                                {transaction.contestant.contest.name}
                              </Link>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 group-hover:text-white transition-colors">
                              {formatPrice(transaction.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 group-hover:text-white transition-colors">
                              {transaction.votes_count}
                            </td>
                            <td className="p-4 text-sm">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentMethodColor(transaction.payment_method)}`}>
                                {formatPaymentMethod(transaction.payment_method)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 group-hover:text-white transition-colors">
                              {transaction.phone_number || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 group-hover:text-white transition-colors">
                              {formatDate(transaction.created_at)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">
                  Page {currentPage} sur {totalPages}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </ClientLayout>
  );
};

export default TransactionList;
